import { BOARD_START_HOUR, BOARD_END_HOUR, type BoardSpan } from '../../../shared/lib/schedule'

/**
 * The drawn window, taken from `shared/lib/schedule` rather than restated here.
 *
 * The two used to be independent literals that happened to agree. They cannot be allowed to drift:
 * every span this widget renders now comes from `toBoardSpan`, which measures minutes from
 * `BOARD_START_HOUR` — so a window mismatch would silently offset every block on the grid.
 */
export const CAL_START_HOUR = BOARD_START_HOUR
export const CAL_END_HOUR = BOARD_END_HOUR

/**
 * Fixed height of the sticky day-column header.
 * Must match the spacer in the time-axis column so labels align with gridlines.
 * Breakdown: py-2 (16px) + text-sm dayName (20px) + text-2xl leading-tight dayNum (30px) + text-xs monthName (16px) = 82px
 */
export const CAL_HEADER_HEIGHT = 82
/**
 * Height of the one-day view's sticky date strip, which stands in for the 82px column header.
 * Must match the `h-9` on `DayStrip`. The time axis needs no spacer for it: the strip sits above
 * the whole grid row rather than inside a column, so the axis and the column start level.
 */
export const DAY_STRIP_HEIGHT = 36
export const CAL_TOTAL_MINUTES = (CAL_END_HOUR - CAL_START_HOUR) * 60

/**
 * Fixed pixel height per 1 hour slot.
 *
 * 96, not the 64 this used to be. At 64 a 30-minute job was 32px tall and a 15-minute Anfahrt 16px —
 * less room than the two text rows the block wanted, so `overflow-hidden` sliced the second line
 * through the middle of its glyphs. The blocks below now measure what fits before they render, but
 * that only stops the clipping; it does not create room. 96 is the room: a 30-minute job is 48px,
 * enough for the time and the location, and a 15-minute drive is 24px, enough for its own label.
 *
 * The cost is a taller day — 16h × 96 = 1536px instead of 1024 — which is scrolling on a phone that
 * already scrolls. Every other measurement on the grid derives from this constant, including the
 * drag maths and the current-time line, so this is the only place the scale is decided.
 */
export const HOUR_HEIGHT = 96

/**
 * Shortest a block may be drawn, whatever its duration.
 *
 * One 14px line box plus its padding — the point below which no text fits and `blockDensity` drops
 * the block to a bare colour bar. Tapping is handled separately: `CalendarBlockShell` extends the
 * hit area to `MIN_TAP_PX` without inflating the paint, so a 10-minute entry stays honest about its
 * length and still catches a thumb.
 */
export const MIN_BLOCK_PX = 16

/**
 * Shortest a block's *hit area* may be — WCAG 2.2 target size, near enough for a thumb in a work
 * glove. `CalendarBlockShell` grows the target to this around a shorter block; the paint stays at
 * the block's real height.
 */
export const MIN_TAP_PX = 28

/** Pixels per minute — the conversion factor for everything below. */
export const MINUTE_HEIGHT = HOUR_HEIGHT / 60

/** Total pixel height of the scrollable time grid */
export const CAL_TOTAL_HEIGHT = (CAL_END_HOUR - CAL_START_HOUR) * HOUR_HEIGHT

export const CAL_HOURS = Array.from(
  { length: CAL_END_HOUR - CAL_START_HOUR + 1 },
  (_, i) => CAL_START_HOUR + i,
)

/** Returns top offset in pixels for a given ISO datetime */
export function getTopPx(dateIso: string): number {
  const d = new Date(dateIso)
  const minuteOfDay = d.getHours() * 60 + d.getMinutes()
  const offset = minuteOfDay - CAL_START_HOUR * 60
  return Math.max(0, (offset / 60) * HOUR_HEIGHT)
}

/** Returns height in pixels for a given duration in minutes */
export function getHeightPx(durationMinutes: number): number {
  return Math.max(0, (durationMinutes / 60) * HOUR_HEIGHT)
}

export function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`
}

/**
 * Decimal hour (e.g. 8.5 for 08:30) of a **shift boundary**, read in UTC.
 *
 * UTC on purpose, and only correct for `shiftStart` / `shiftEnd`: those are a wall-clock time-of-day
 * stored inside a datetime, not a real instant. Entry times are the opposite — real instants, read
 * local by `getTopPx` and by `toBoardSpan`. `shared/lib/schedule/geometry.ts` documents the same
 * split for the board, and `shiftHourFromIso` there is this function's counterpart.
 */
export function getHourFromIso(iso: string): number {
  const d = new Date(iso)
  return d.getUTCHours() + d.getUTCMinutes() / 60
}

/* ------------------------------------------------------------------ */
/*  Bridge: shared minute domain → this widget's vertical pixels       */
/* ------------------------------------------------------------------ */

/**
 * `shared/lib/schedule` is axis-agnostic in minutes and horizontal in pixels. These two functions
 * are the entire seam: everything the board draws with `toXPx`/`spanWidthPx`, this widget draws
 * with `spanTopPx`/`spanHeightPx` off the very same `BoardSpan`.
 */
export const spanTopPx = (span: BoardSpan): number => span.startMin * MINUTE_HEIGHT

/** `minPx` keeps a very short block tall enough to stay legible and grabbable. */
export const spanHeightPx = (span: BoardSpan, minPx: number): number =>
  Math.max((span.endMin - span.startMin) * MINUTE_HEIGHT, minPx)
